import { useCallback, useEffect, useState } from "react";
import { istAppleGeraet } from "@/lib/appleGeraet";

/**
 * Zustand und Steuerung für den Installationshinweis.
 *
 * Zwei Wege führen zur Installation:
 * - Chrome/Edge/Android feuern "beforeinstallprompt"; das Event wird
 *   abgefangen und später über prompt() ausgelöst.
 * - iOS/Safari kennt das Event nicht — dort bleibt nur die Anleitung
 *   "Teilen ▸ Zum Home-Bildschirm".
 */

const DISMISSED_KEY = "cf-pwa-install-dismissed";

/** Nicht in lib.dom typisiert, weil bislang nur Chromium-Browser es feuern. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export type InstallPromptMode = "hidden" | "prompt" | "ios-instructions";

export interface InstallPromptState {
  mode: InstallPromptMode;
  install: () => Promise<void>;
  dismiss: () => void;
}

function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  const iosStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone;
  return window.matchMedia("(display-mode: standalone)").matches || iosStandalone === true;
}

/**
 * iOS-Safari: ein Apple-Gerät (`istAppleGeraet`, dort auch der iPadOS-Fall) und
 * darauf Safari — nur dort gibt es „Zum Home-Bildschirm", und nur darum geht es
 * hier.
 */
function isIosSafari(): boolean {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent;
  return istAppleGeraet() && /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
}

function wasDismissed(): boolean {
  try {
    return window.localStorage.getItem(DISMISSED_KEY) === "1";
  } catch {
    // Privater Modus oder blockierter Storage: dann eben jedes Mal anzeigen.
    return false;
  }
}

export function useInstallPrompt(): InstallPromptState {
  const [deferredEvent, setDeferredEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [mode, setMode] = useState<InstallPromptMode>("hidden");

  useEffect(() => {
    if (isStandalone() || wasDismissed()) return;

    if (isIosSafari()) setMode("ios-instructions");

    const onBeforeInstallPrompt = (event: Event) => {
      // Verhindert den browsereigenen Mini-Infobalken, damit der Hinweis im
      // Produkt und nicht doppelt in der Browser-UI erscheint.
      event.preventDefault();
      setDeferredEvent(event as BeforeInstallPromptEvent);
      setMode("prompt");
    };
    const onInstalled = () => {
      setDeferredEvent(null);
      setMode("hidden");
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = useCallback(() => {
    setMode("hidden");
    try {
      window.localStorage.setItem(DISMISSED_KEY, "1");
    } catch {
      // Ohne Storage bleibt es beim Ausblenden für diese Sitzung.
    }
  }, []);

  const install = useCallback(async () => {
    if (!deferredEvent) return;
    await deferredEvent.prompt();
    await deferredEvent.userChoice;
    // Das Event ist nach prompt() verbraucht und lässt sich nicht wiederverwenden.
    setDeferredEvent(null);
    setMode("hidden");
  }, [deferredEvent]);

  return { mode, install, dismiss };
}
